import { SellerAPI } from "../../services/apiService";

export const getAllProducts = async () => {
  const response = await SellerAPI.getAllProducts();
  return response.data;
};

export const getProductDetail = async (productId) => {
  const response = await SellerAPI.getProductDetail(productId);
  // console.log("Seller Product data:", response.data);
  return response.data;
};

export const createProduct = async (data) => {
  const payload = new FormData();

  // Ensure boolean-like fields are numeric (0 or 1)
  if (typeof data.is_reverse_auction === "boolean") {
    data.is_reverse_auction = data.is_reverse_auction ? 1 : 0;
  }

  const cleanDescription = (desc) => {
    if (
      typeof desc === "string" &&
      desc.startsWith("<p>") &&
      desc.endsWith("</p>")
    ) {
      return desc.replace(/^<p>|<\/p>$/g, "").trim();
    }
    return desc;
  };

  //  Only the fields currently accepted by backend
  const requiredKeys = [
    "category_id",
    "subcategory_id",
    "name",
    "description",
    "price",
    "quantity",
    "location",
    "unit_of_measurement",
    "incoterm",
    "payment_term",
    "lead_time",
    "delivery_port",
    "delivery_radius",
    "seller_latitude",
    "seller_longitude",
    "metal_code",
    "sorting_type",
    "attachment",
    "waste_percentage",
    "waste_range",
    // "toxic_level",
    "hazard_types",
    "hazard_description",
    "price_per_unit",
    // "weight",
    "currency",
    "estimated_price",
    // "is_reverse_auction",
    "minimum_asking_price",
    "bid_increment",
    "bid_closed_at",
    // "reverse_close_price",
    "auction_status",
    "allow_bidding",
    "save_as_draft",
  ];

  // append only required keys
  requiredKeys.forEach((k) => {
    let value = data[k];
    if (value === undefined || value === null) value = "";

    // Clean description field
    if (k === "description") {
      value = cleanDescription(value);
    }

    // numeric fields
    const numericKeys = [
      "price",
      "quantity",
      "lead_time",
      "weight",
      "price_per_unit",
      "estimated_price",
      "minimum_asking_price",
      "bid_increment",
      "reverse_close_price",
    ];
    if (numericKeys.includes(k)) {
      value = value === "" ? "" : Number(value);
    }

    payload.append(k, value);
  });

  // Add hazard_types array properly
  if (Array.isArray(data.hazard_types) && data.hazard_types.length > 0) {
    data.hazard_types.forEach((h, i) => {
      payload.append(`hazard_types[${i}]`, h);
    });
  } else {
    console.log("hazard_types is empty or not an array:", data.hazard_types);
    // If hazard_types is null or empty, append it as an array with a single null element
    payload.append("hazard_types[0]", [null]);
  }

  // Set currency based on data or default to empty
  payload.append("currency", data.currency || "");

  //  specifications — only append if available
  if (Array.isArray(data.specifications)) {
    data.specifications.forEach((s, i) => {
      if (s.field_id && s.value) {
        payload.append(`specifications[${i}][field_id]`, s.field_id);
        payload.append(`specifications[${i}][value]`, s.value);
      }
    });
  }

  //  faqs — required, even if empty
  if (Array.isArray(data.faqs)) {
    data.faqs.forEach((f, i) => {
      payload.append(`faqs[${i}][question]`, f?.question || "");
      payload.append(`faqs[${i}][answer]`, f?.answer || "");
    });
  } else {
    payload.append(`faqs[0][question]`, "");
    payload.append(`faqs[0][answer]`, "");
  }

  //  Files (required — use placeholder if missing)
  if (data.image) {
    const imageFile = data.image[0] || data.image;
    if (imageFile instanceof File) {
      payload.append("image", imageFile);
    } else {
      payload.append("image", "");
    }
  } else {
    payload.append("image", ""); // backend requires image field
  }

  if (data.video) {
    const videoFile = data.video[0] || data.video;
    if (videoFile instanceof File) {
      payload.append("video", videoFile);
    } else {
      payload.append("video", "");
    }
  } else {
    payload.append("video", ""); // backend requires video field
  }

  // Debug log
  for (let [key, val] of payload.entries()) {
    console.log(`${key}:`, val);
  }
  console.groupEnd();
  console.group("FormData payload", payload);

  const response = await SellerAPI.addProduct(payload);
  return response.data;
};

export const updateProduct = async (id, data) => {
  const payload = new FormData();

  // Ensure boolean-like fields are numeric (0 or 1)
  if (typeof data.is_reverse_auction === "boolean") {
    data.is_reverse_auction = data.is_reverse_auction ? 1 : 0;
  }
  data.allow_bidding = Number(data.allow_bidding);

  const cleanDescription = (desc) => {
    if (
      typeof desc === "string" &&
      desc.startsWith("<p>") &&
      desc.endsWith("</p>")
    ) {
      return desc.replace(/^<p>|<\/p>$/g, "").trim();
    }
    return desc;
  };

  //  Only the fields currently accepted by backend
  const requiredKeys = [
    "category_id",
    "subcategory_id",
    "name",
    "description",
    "price",
    "quantity",
    "location",
    "unit_of_measurement",
    "incoterm",
    "payment_term",
    "lead_time",
    "delivery_port",
    "delivery_radius",
    "seller_latitude",
    "seller_longitude",
    "metal_code",
    "sorting_type",
    "attachment",
    "waste_percentage",
    "waste_range",
    "toxic_level",
    "hazard_types",
    "hazard_description",
    "price_per_unit",
    // "weight",
    "currency",
    "estimated_price",
    "is_reverse_auction",
    "minimum_asking_price",
    "bid_increment",
    "bid_closed_at",
    "reverse_close_price",
    "auction_status",
    "save_as_draft",
    "allow_bidding",
  ];

  requiredKeys.forEach((k) => {
    let value = data[k];
    if (value === undefined || value === null) value = "";

    if (k === "description") {
      value = cleanDescription(value);
    }

    const numericKeys = [
      "price",
      "quantity",
      "lead_time",
      "weight",
      "price_per_unit",
      "estimated_price",
      "minimum_asking_price",
      "bid_increment",
      "reverse_close_price",
    ];
    if (numericKeys.includes(k)) {
      value = value === "" ? "" : Number(value);
    }

    payload.append(k, value);
  });

  // Add hazard_types array properly
  if (Array.isArray(data.hazard_types)) {
    data.hazard_types.forEach((h, i) => {
      payload.append(`hazard_types[${i}]`, h);
    });
  }

  // specifications
  if (Array.isArray(data.specifications)) {
    data.specifications.forEach((s, i) => {
      if (s.field_id && s.value) {
        payload.append(`specifications[${i}][field_id]`, s.field_id);
        payload.append(`specifications[${i}][value]`, s.value);
      }
    });
  }

  // faqs
  if (Array.isArray(data.faqs)) {
    data.faqs.forEach((f, i) => {
      payload.append(`faqs[${i}][question]`, f?.question || "");
      payload.append(`faqs[${i}][answer]`, f?.answer || "");
    });
  } else {
    payload.append(`faqs[0][question]`, "");
    payload.append(`faqs[0][answer]`, "");
  }

  // Files
  if (data.image) {
    const imageFile = data.image[0] || data.image;
    if (imageFile instanceof File) {
      payload.append("image", imageFile);
    }
  }

  if (data.video) {
    const videoFile = data.video[0] || data.video;
    if (videoFile instanceof File) {
      payload.append("video", videoFile);
    }
  }

  // Debug log
  console.group("FormData payload for update");
  for (let [key, val] of payload.entries()) {
    console.log(`${key}:`, val);
  }
  console.groupEnd();

  const response = await SellerAPI.updateProduct(id, payload);
  return response.data;
};

export const deleteProduct = async (id, data) => {
  const response = await SellerAPI.deleteProduct(id, data);
  return response.data;
};

export const trashProducts = async () => {
  const response = await SellerAPI.trashProducts();
  return response.data;
};

export const restoreProducts = async (id, data) => {
  const response = await SellerAPI.restoreProducts(id, data);
  return response.data;
};

export const calculatePrice = async (data) => {
  const response = await SellerAPI.calculatePrice(data);
  return response.data;
};

export const getDashBoardData = async () => {
  const response = await SellerAPI.getDashboard();
  return response.data;
};

export const metalPrice = async (data) => {
  const response = await SellerAPI.metalPrice(data);
  return response.data;
};

export const getMetalCode = async (data) => {
  const response = await SellerAPI.getMetalCode(data);
  return response.data;
};

export const getStockList = async () => {
  const response = await SellerAPI.getStockList();
  return response.data;
};

export const updateStock = async (id, data) => {
  const response = await SellerAPI.updateStock(id, data);
  return response.data;
};

export const getBids = async () => {
  const response = await SellerAPI.getBids();
  return response.data;
};

export const updateBidStatus = async (id, data) => {
  const response = await SellerAPI.updateBidStatus(id, data);
  return response.data;
};

export const getOrders = async () => {
  const response = await SellerAPI.getOrders();
  return response.data;
};

export const getOrderDetails = async (id) => {
  const response = await SellerAPI.getOrderDetails(id);
  return response.data;
};

export const updateOrderStatus = async (id, data) => {
  const response = await SellerAPI.updateOrderStatus(id, data);
  return response.data;
};

export const getShippingPartners = async () => {
  const response = await SellerAPI.getShippingPartners();
  return response.data;
};

export const updateShippingPartner = async (id, data) => {
  const response = await SellerAPI.updateShippingPartner(id, data);
  return response.data;
};

export const updateTrackingNumber = async (data) => {
  const response = await SellerAPI.updateTrackingNumber(data);
  return response.data;
};

export const getInvoiceList = async () => {
  const response = await SellerAPI.getInvoiceList();
  return response.data;
};

export const getInvoiceDetails = async (id) => {
  const response = await SellerAPI.getInvoiceDetails(id);
  return response.data;
};

export const getCategories = async () => {
  const response = await SellerAPI.getCategories();
  return response.data;
};

export const getSubcategories = async (id) => {
  const response = await SellerAPI.getSubcategories(id);
  return response.data;
};

export const getSpecifications = async (id) => {
  const response = await SellerAPI.getSpecifications(id);
  return response.data;
};

export const getReverseBids = async () => {
  const response = await SellerAPI.getReverseBids();
  return response.data;
};

export const storeReverseBid = async (id, data) => {
  const response = await SellerAPI.storeReverseBid(id, data);
  return response.data;
};

export const getReverseBidOrders = async () => {
  const response = await SellerAPI.getReverseBidOrders();
  return response.data;
};

export const uploadCertificates = async (formData) => {
  const response = await SellerAPI.uploadCertificate(formData);
  return response.data;
};

export const getCertificatesList = async () => {
  const response = await SellerAPI.getCertificates();
  // console.log("res data:", response.data);
  return response.data;
};

export const getPaymentMethods = async () => {
  const response = await SellerAPI.getPaymentMethods();
  return response.data;
};

export const getAccountStatus = async () => {
  const response = await SellerAPI.getAccountStatus();
  return response.data;
};

export const generateDescription = async (data) => {
  console.log("🚨 SELLER SERVICE CALLED", data);
  const response = await SellerAPI.generateDescription(data);
  return response.data;
};

export const generateFaqs = async (data) => {
  const response = await SellerAPI.generateFaqs(data);
  return response.data;
};

export const generateVideoUrl = async (id) => {
  const response = await SellerAPI.generateVideoUrl(id);
  return response.data;
};

export const getReceivedOffers = async () => {
  const response = await SellerAPI.getReceivedOffers();
  return response.data;
};

export const respondCounterOffer = async (id, data) => {
  const response = await SellerAPI.respondCounterOffer(id, data);
  return response.data;
};

export const receivedOffersDetail = async (id) => {
  const response = await SellerAPI.receivedOffersDetail(id)
  return response.data
}

export const getOutOfRange = async () => {
  const response = await SellerAPI.getOutOfRangeList()
  return response.data
}

export const approveRequest = async (id, data) => {
  const response = await SellerAPI.approveRequest(id, data)
  return response.data
}

export const rejectRequest = async (id) => {
  const response = await SellerAPI.rejectRequest(id)
  return response.data
}

export const onboardingList = async () => {
  const response = await SellerAPI.onboardingList()
  return response.data
}

export const onboardingSubmit = async (data) => {
  const response = await SellerAPI.onboardingSubmit(data)
  return response.data
}

export const sellerService = {
  getAllProducts,
  getProductDetail,
  createProduct,
  updateProduct,
  deleteProduct,
  trashProducts,
  restoreProducts,
  calculatePrice,
  getDashBoardData,
  metalPrice,
  getMetalCode,
  getStockList,
  updateStock,
  getBids,
  updateBidStatus,
  getOrders,
  getOrderDetails,
  updateOrderStatus,
  getShippingPartners,
  updateShippingPartner,
  updateTrackingNumber,
  getInvoiceList,
  getInvoiceDetails,
  getCategories,
  getSubcategories,
  getSpecifications,
  getReverseBids,
  storeReverseBid,
  getReverseBidOrders,
  uploadCertificates,
  getCertificatesList,
  getPaymentMethods,
  getAccountStatus,
  generateDescription,
  generateFaqs,
  generateVideoUrl,
  getReceivedOffers,
  respondCounterOffer,
  receivedOffersDetail,
  getOutOfRange,
  approveRequest,
  rejectRequest,
  onboardingList,
  onboardingSubmit,
};
